// @ts-nocheck
import { GridItem, LazyImage, Typography } from "@insite/mobius/index";

import translate from "@insite/client-framework/Translate";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getBoldedText from "@insite/client-framework/Common/Utilities/getPatternBolded";
import { autocompleteSearch } from "@insite/client-framework/Services/AutocompleteService";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import { AutocompleteModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import useSearch from "@store/Framework/Common/Hooks/useSearch";
import setBuyingListProduct from "@store/Framework/Store/Bunzl/Handlers/SetBuyingListProduct";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

/* Bunzl Custom Code - Start */
import DynamicDropdown from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { productSelectorStyles } from "@store/Overrides/Components/ProductSelector";
/* Bunzl Custom Code - End */

export interface ProductModalSearchProps {
    buyingList: WishListModel;
}

export const createOptions = (results: AutocompleteModel, query: string) => {
    return (
        results?.products?.map(product => {
            product.displayTitle = getBoldedText(product.title, query);
            product.displayErpNumber = getBoldedText(product.erpNumber, query);
            return {
                optionText: product.title,
                optionValue: product.id || undefined,
                rowChildren: (
                    <StyledWrapper
                        {...productSelectorStyles.optionWrapper}
                        data-test-selector={`productSelector_search_${product.id}`}
                    >
                        <StyledWrapper {...productSelectorStyles.imageWrapper}>
                            <LazyImage {...productSelectorStyles.productImage} src={product.image} />
                        </StyledWrapper>
                        <StyledWrapper {...productSelectorStyles.infoWrapper}>
                            <Typography
                                {...productSelectorStyles.autocompleteTitleText}
                                data-test-selector="productSelector_ProductTitle"
                            >
                                {product.displayTitle || product.title}
                            </Typography>
                            <Typography {...productSelectorStyles.autocompleteErpText}>
                                {product.displayErpNumber || product.erpNumber}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ),
            };
        }) || []
    );
};

const ProductModalSearch: React.FC<ProductModalSearchProps> = ({ buyingList }) => {
    const dispatch = useDispatch();
    const selectedProductInfo = useAppSelector(state => state.components.productSelector.selectedProductInfo);

    const { updateBuyingLists, loading } = useBuyingList();
    const [autoCompleteResults, setAutoCompleteResults] = useState<AutocompleteModel | undefined>();
    const [autoCompleteLoading, setAutoCompleteLoading] = React.useState(false);

    const handleSelection = (value: string | undefined) => {
        if (!value) {
            return;
        }

        setSearchTerm("");
        setAutoCompleteResults(undefined);
        const selectedProduct = autoCompleteResults?.products?.find(result => result.id === value);
        if (!selectedProduct) {
            return;
        }

        if (!selectedProduct.styleParentId) {
            dispatch(
                setBuyingListProduct({
                    productId: value,
                }),
            );
        } else {
            updateBuyingLists(buyingList, { itemsToAdd: [value] });
        }
    };

    const handleChange = async (searchTerm: string) => {
        if (!searchTerm) {
            setAutoCompleteResults(undefined);
            return;
        }
        setAutoCompleteLoading(true);
        const results = await autocompleteSearch({
            query: searchTerm,
            productEnabled: true,
            brandEnabled: false,
            categoryEnabled: false,
            contentEnabled: false,
        });

        setAutoCompleteResults(results);
        setAutoCompleteLoading(false);
    };
    const [searchTerm, setSearchTerm] = useSearch(handleChange);
    const handleClear = () => {
        handleChange("");
        dispatch(clearProduct());
    };
    let options: ReturnType<typeof createOptions> | undefined;
    if (autoCompleteResults) {
        options = createOptions(autoCompleteResults, searchTerm);
    }
    return (
        <>
            <GridItem width={12}>
                <Typography variant="h4"> {translate("Add Products to List")} </Typography>
            </GridItem>
            <GridItem width={[12, 12, 12, 6, 6]}>
                <DynamicDropdown
                    isLoading={autoCompleteLoading || loading}
                    onInputChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                    filterOption={() => true}
                    selected={selectedProductInfo?.productId}
                    placeholder={translate("Search For Products to Add")}
                    onSelectionChange={handleSelection}
                    options={options || []}
                    clearOnClick={handleClear}
                    {...productSelectorStyles.searchDynamicDropdown}
                />
            </GridItem>
        </>
    );
};

export default ProductModalSearch;
